import React, { useEffect } from 'react';
import { motion } from 'motion/react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../components/logo/Logo';
import { Button } from '../components/common/Button';
import { Helmet } from 'react-helmet';
import { useAuth } from '../hooks/useAuth';
import { ArrowRightIcon, PlayIcon, TimerIcon } from '@radix-ui/react-icons';

const LandingPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Zilean";
  }, []);

  const handleDashboardClick = () => {
    navigate('/home');
  };

  return (
    <>
      <style>
        {`
          mark {
            margin: 0 -0.4em;
            padding: 0.1em 0.4em;
            border-radius: 0.8em 0.3em;
            background: transparent;
            background-image: linear-gradient(
              to right,
              rgba(255, 225, 0, 0.1),
              rgba(255, 225, 0, 0.7) 4%,
              rgba(255, 225, 0, 0.3)
            );
            -webkit-box-decoration-break: clone;
            box-decoration-break: clone;
          }
        `}
      </style>
      <div className="min-h-screen bg-gradient-to-b from-background to-neutral-50 dark:from-background dark:to-neutral-900 flex flex-col overflow-x-hidden">
        <Helmet>
          <title>Zilean</title>
          <meta name="description" content="Let's make your daily tracking effortless! Whether you're working, learning, or building new habits, get a clear view of where your time goes with charts, timelines, and smart insights." />
          <meta name="keywords" content="time tracking, activity tracker, productivity" />
          <meta property="og:title" content="Zilean" />
          <meta property="og:description" content="Let's make your daily tracking effortless! Get a clear view of where your time goes." />
          <meta property="og:type" content="website" />
          <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Space+Grotesk:wght@500;700&display=swap" rel="stylesheet" />
        </Helmet>

        {/* Header */}
        <header className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Logo />
          <div className="flex gap-4">
            {isAuthenticated ? (
              <Button className="bg-white text-primary hover:bg-secondary-100" size="sm" onClick={handleDashboardClick}>
                Dashboard
                <ArrowRightIcon className="ml-2" />
              </Button>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm">Log in</Button>
                </Link>
                <Link to="/signup">
                  <Button size="sm">Sign up</Button>
                </Link>
              </>
            )}
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center w-full">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Left Content */}
              <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
                <motion.h1 
                  className="text-5xl md:text-6xl lg:text-6xl font-bold mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <mark>Know Where Your Time Goes</mark>
                </motion.h1>
                
                <motion.p 
                  className="text-base text-neutral-500 dark:text-neutral-400 mb-8 max-w-lg leading-loose tracking-wide"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <div className="space-y-2 mb-4">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.3 }}
                      className="font-semibold text-primary-500"
                    >
                      → Playing the piano?
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.5 }}
                      className="font-semibold text-primary-500"
                    >
                      → Learning a new language?
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.7 }}
                      className="font-semibold text-primary-500"
                    >
                      → Working on the next big thing?
                    </motion.div>
                  </div>
                  Let's make your daily tracking effortless!
                  <div className="mt-2 text-neutral-400 dark:text-neutral-500 text-sm">
                    No subscriptions. No fluff. Just time tracking.
                  </div>
                </motion.p>
                
                <motion.div
                  className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <Link to="/signup">
                    <Button 
                      size="lg" 
                      className="gap-2 shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <PlayIcon className="w-5 h-5" />
                      Start Tracking
                    </Button>
                  </Link>
                </motion.div>
              </div>

              {/* Right Content - Activity Details Image */}
              <motion.div
                className="hidden lg:flex justify-center items-center"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="relative w-full">
                  {/* Decorative background elements */}
                  <div className="absolute -inset-4 bg-gradient-to-r from-primary-500/10 to-secondary-500/10 rounded-3xl blur-lg"></div>
                  <motion.div 
                    className="absolute -inset-0.5 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 rounded-2xl"
                    animate={{
                      opacity: [0.5, 0.8, 0.5],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  ></motion.div>
                  
                  {/* Floating dots decoration */}
                  <motion.div
                    className="absolute -right-4 -top-4 w-16 h-16 bg-primary-500/10 rounded-full blur-sm"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0.5, 0.3],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  ></motion.div>
                  <motion.div
                    className="absolute -left-4 -bottom-4 w-12 h-12 bg-secondary-500/10 rounded-full blur-sm"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0.5, 0.3],
                    }}
                    transition={{
                      duration: 4,
                      delay: 1,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  ></motion.div>

                  {/* Main image */}
                  <div className="relative">
                    <img 
                      draggable="false"
                      src="img/landing/landing_1.png" 
                      alt="Activity Details Dashboard" 
                      className="w-full h-auto rounded-xl"
                    />
                    
                    {/* Shine effect */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -rotate-15 rounded-xl"
                      animate={{
                        opacity: [0, 0.5, 0],
                        x: ['-100%', '200%', '200%']
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        repeatDelay: 4,
                        ease: "easeInOut"
                      }}
                    ></motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </main>

        {/* Minimal Footer */}
        <footer className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center text-sm text-neutral-500 font-['Inter']">
            <span>&copy; {new Date().getFullYear()} Zilean</span>
            <Link to="/privacy-policy" className="hover:text-primary-500">Privacy Policy</Link>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPage; 